/**
 * Placement math for the tour card — pure, DOM-free, unit-tested.
 *
 * This is the flip / shift / arrow subset of a positioning library and nothing
 * else. Taking a dependency for it would mean 15-20KB for generality this tour
 * never uses, in a repo that has no positioning dependency at all. Numbers in,
 * numbers out, so the tests are a table rather than a JSDOM layout fight.
 *
 * Order matters and is the part that is easy to get wrong: **flip first, then
 * clamp, then derive the arrow from the clamped box.** Deriving the arrow from
 * the pre-clamp position is the classic bug — the card shifts away from the
 * viewport edge and the arrow keeps pointing at where the card used to be.
 */

import type { TourAlign, TourSide } from './tourTypes'

export type Rect = { x: number; y: number; width: number; height: number }
export type Size = { width: number; height: number }

export type PlacementInput = {
  anchor: Rect
  widget: Size
  viewport: Size
  preferred: TourSide | 'auto'
  align: TourAlign
  /** Gap between anchor and card, and the minimum inset from the viewport edge. */
  margin?: number
  arrowSize?: number
}

export type Placement = {
  side: TourSide
  left: number
  top: number
  /** Arrow centre in viewport coordinates, or `null` when the card is clamped away from the anchor. */
  arrow: { x: number; y: number } | null
  /** True when the card had to be pushed to stay on screen. */
  clamped: boolean
  /** True when no side fit and we picked the roomiest one anyway. */
  overflowed: boolean
}

const DEFAULT_MARGIN = 12
const DEFAULT_ARROW = 10
/** Keep the arrow off the card's rounded corners. */
const ARROW_CORNER_INSET = 16

const AUTO_ORDER: TourSide[] = ['bottom', 'top', 'right', 'left']

const OPPOSITE: Record<TourSide, TourSide> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
}

const clamp = (value: number, lo: number, hi: number): number => {
  // A viewport narrower than the card makes `lo > hi`; prefer the low edge so
  // the card stays reachable instead of sliding off the right.
  if (hi < lo) return lo
  return Math.min(Math.max(value, lo), hi)
}

/** Space available on each side of the anchor, after the gap. */
export function sideSpace(anchor: Rect, viewport: Size, margin: number): Record<TourSide, number> {
  return {
    top: anchor.y - margin,
    bottom: viewport.height - (anchor.y + anchor.height) - margin,
    left: anchor.x - margin,
    right: viewport.width - (anchor.x + anchor.width) - margin,
  }
}

const requiredFor = (side: TourSide, widget: Size): number =>
  side === 'top' || side === 'bottom' ? widget.height : widget.width

/** Unclamped position for a given side + alignment. */
function positionOn(side: TourSide, input: Required<Pick<PlacementInput, 'anchor' | 'widget' | 'align' | 'margin'>>): { left: number; top: number } {
  const { anchor, widget, align, margin } = input
  const alongX = () => {
    if (align === 'start') return anchor.x
    if (align === 'end') return anchor.x + anchor.width - widget.width
    return anchor.x + anchor.width / 2 - widget.width / 2
  }
  const alongY = () => {
    if (align === 'start') return anchor.y
    if (align === 'end') return anchor.y + anchor.height - widget.height
    return anchor.y + anchor.height / 2 - widget.height / 2
  }
  switch (side) {
    case 'top':
      return { left: alongX(), top: anchor.y - widget.height - margin }
    case 'bottom':
      return { left: alongX(), top: anchor.y + anchor.height + margin }
    case 'left':
      return { left: anchor.x - widget.width - margin, top: alongY() }
    case 'right':
    default:
      return { left: anchor.x + anchor.width + margin, top: alongY() }
  }
}

/**
 * Choose a side and a position for the card.
 *
 * 1. Try `preferred`; if it does not fit, try its opposite, then the rest in a
 *    stable order. `'auto'` walks bottom → top → right → left.
 * 2. If nothing fits, keep the side with the most room and mark `overflowed`.
 * 3. Clamp into the viewport with `margin` inset.
 * 4. Derive the arrow from the *clamped* box, dropping it when the card no
 *    longer touches the anchor's span.
 */
export function placeWidget(input: PlacementInput): Placement {
  const margin = input.margin ?? DEFAULT_MARGIN
  const arrowSize = input.arrowSize ?? DEFAULT_ARROW
  const { anchor, widget, viewport, align } = input

  const space = sideSpace(anchor, viewport, margin)
  const preferred = input.preferred
  const order: TourSide[] =
    preferred === 'auto'
      ? AUTO_ORDER
      : [preferred, OPPOSITE[preferred], ...AUTO_ORDER.filter((s) => s !== preferred && s !== OPPOSITE[preferred])]

  let side: TourSide | null = null
  for (const candidate of order) {
    if (space[candidate] >= requiredFor(candidate, widget)) {
      side = candidate
      break
    }
  }
  let overflowed = false
  if (!side) {
    overflowed = true
    // Nothing fits — take the roomiest of the sides we were willing to consider,
    // preserving `order` as the tiebreak so placement stays deterministic.
    side = order.reduce((best, candidate) =>
      space[candidate] - requiredFor(candidate, widget) > space[best] - requiredFor(best, widget) ? candidate : best,
    order[0])
  }

  const raw = positionOn(side, { anchor, widget, align, margin })
  const left = clamp(raw.left, margin, viewport.width - widget.width - margin)
  const top = clamp(raw.top, margin, viewport.height - widget.height - margin)
  const clamped = left !== raw.left || top !== raw.top

  return { side, left, top, arrow: arrowFor(side, anchor, { left, top }, widget, arrowSize), clamped, overflowed }
}

/**
 * Arrow centre in viewport coordinates, computed against the final card box.
 * Returns `null` when the anchor's span no longer overlaps the card's edge —
 * an arrow pointing at nothing reads worse than no arrow.
 */
export function arrowFor(
  side: TourSide,
  anchor: Rect,
  card: { left: number; top: number },
  widget: Size,
  arrowSize: number,
): { x: number; y: number } | null {
  const horizontal = side === 'top' || side === 'bottom'
  if (horizontal) {
    const anchorCentre = anchor.x + anchor.width / 2
    const lo = card.left + ARROW_CORNER_INSET
    const hi = card.left + widget.width - ARROW_CORNER_INSET
    if (anchorCentre < card.left || anchorCentre > card.left + widget.width) return null
    return {
      x: clamp(anchorCentre, lo, hi),
      y: side === 'top' ? card.top + widget.height : card.top - arrowSize,
    }
  }
  const anchorCentre = anchor.y + anchor.height / 2
  const lo = card.top + ARROW_CORNER_INSET
  const hi = card.top + widget.height - ARROW_CORNER_INSET
  if (anchorCentre < card.top || anchorCentre > card.top + widget.height) return null
  return {
    x: side === 'left' ? card.left + widget.width : card.left - arrowSize,
    y: clamp(anchorCentre, lo, hi),
  }
}

/**
 * The four backdrop rectangles that surround the anchor, leaving a real hole.
 *
 * Four rects rather than an SVG mask or `box-shadow: 0 0 0 9999px`, because the
 * hole is then genuinely uncovered: clicks, hover and focus reach the live
 * element with no pointer-event forwarding. That is what makes a
 * `click_anchor` step work at all.
 */
export function backdropRects(hole: Rect, viewport: Size): Rect[] {
  const x = clamp(hole.x, 0, viewport.width)
  const y = clamp(hole.y, 0, viewport.height)
  const right = clamp(hole.x + hole.width, 0, viewport.width)
  const bottom = clamp(hole.y + hole.height, 0, viewport.height)
  return [
    { x: 0, y: 0, width: viewport.width, height: y },
    { x: 0, y: bottom, width: viewport.width, height: Math.max(0, viewport.height - bottom) },
    { x: 0, y, width: x, height: Math.max(0, bottom - y) },
    { x: right, y, width: Math.max(0, viewport.width - right), height: Math.max(0, bottom - y) },
  ].filter((r) => r.width > 0 && r.height > 0)
}

/** Grow a rect by `padding` on every side, clamped to the viewport. */
export function inflate(rect: Rect, padding: number, viewport: Size): Rect {
  const x = Math.max(0, rect.x - padding)
  const y = Math.max(0, rect.y - padding)
  return {
    x,
    y,
    width: Math.min(viewport.width - x, rect.width + padding * 2),
    height: Math.min(viewport.height - y, rect.height + padding * 2),
  }
}

/** Cheap identity check so the rAF re-measure loop can skip unchanged frames. */
export const rectEquals = (a: Rect | null, b: Rect | null): boolean => {
  if (!a || !b) return a === b
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height
}
